import { useCallback, useEffect, useRef } from 'react';

/**
 * Returns a `schedule()` that runs `action` after `delay` ms of quiet
 * (debounce), but never while a previous run is still in flight (mutex) — if
 * `isPending` is true when it would fire, it retries after `retry` ms instead of
 * starting a second run. Used to coalesce a flurry of board changes into a
 * single curate pass and avoid overlapping Steward runs.
 */
export function useDebouncedMutex(
  action: () => void,
  isPending: boolean,
  { delay = 1200, retry = 1000 }: { delay?: number; retry?: number } = {}
) {
  const pendingRef = useRef(isPending);
  pendingRef.current = isPending;
  const actionRef = useRef(action);
  actionRef.current = action;
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const fire = useCallback(() => {
    if (pendingRef.current) {
      timer.current = setTimeout(fire, retry);
      return;
    }
    actionRef.current();
  }, [retry]);

  const schedule = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(fire, delay);
  }, [fire, delay]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return schedule;
}
